import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { billingAPI } from '../../services/api'
import { t } from '../../i18n'
import { PageHeader, Table, Loading, Empty, Chip, cents } from './ui'

const input = 'rounded-lg border-0 bg-white px-3 py-2 text-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-500'
const blank = { name: '', price: '', currency: 'usd', maxServices: '1', maxUsers: '1', features: '' }

const parseFeatures = plan => { try { return JSON.parse(plan.features_json || '[]') } catch { return [] } }
// Empty means "all services" / "no user limit".
const limitText = (value, all) => (value == null ? all : String(value))

// Plan form fields shared by "Add plan" and editing a plan.
function PlanFields({ value, onChange, withCurrency }) {
  const set = key => e => onChange({ ...value, [key]: e.target.value })
  return (
    <>
      <label className="flex-1 min-w-[10rem]">
        <span className="mb-1 block text-xs font-medium text-slate-600">{t('Plan name')}</span>
        <input className={`${input} w-full`} value={value.name} onChange={set('name')} placeholder={t('e.g. Standard')} />
      </label>
      <label className="w-32">
        <span className="mb-1 block text-xs font-medium text-slate-600">{t('Price / month')}</span>
        <input className={`${input} w-full`} type="number" step="0.01" min="0" value={value.price} onChange={set('price')} placeholder="29.00" />
      </label>
      {withCurrency && (
        <label>
          <span className="mb-1 block text-xs font-medium text-slate-600">{t('Currency')}</span>
          <select className={input} value={value.currency} onChange={set('currency')}>
            {['usd', 'omr', 'sar', 'aed', 'qar', 'kwd', 'bhd', 'eur', 'gbp'].map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
        </label>
      )}
      <label>
        <span className="mb-1 block text-xs font-medium text-slate-600">{t('Services')}</span>
        <select className={input} value={value.maxServices} onChange={set('maxServices')}>
          <option value="1">{t('Choose one')}</option>
          <option value="2">{t('Choose two')}</option>
          <option value="">{t('All services')}</option>
        </select>
      </label>
      <label className="w-28">
        <span className="mb-1 block text-xs font-medium text-slate-600">{t('Users')}</span>
        <input className={`${input} w-full`} type="number" min="1" value={value.maxUsers} onChange={set('maxUsers')} placeholder={t('No limit')} />
      </label>
      <label className="basis-full">
        <span className="mb-1 block text-xs font-medium text-slate-600">{t('What the plan includes (one per line)')}</span>
        <textarea className={`${input} w-full`} rows={3} value={value.features} onChange={set('features')} placeholder={t('e.g. Live WhatsApp support')} />
      </label>
    </>
  )
}

const toBody = form => ({
  name: form.name.trim(),
  basePriceCents: Math.round(parseFloat(form.price) * 100),
  maxServices: form.maxServices === '' ? null : Number(form.maxServices),
  maxUsers: form.maxUsers === '' ? null : Number(form.maxUsers),
  features: form.features.split('\n').map(f => f.trim()).filter(Boolean).slice(0, 12),
})

export default function PlatformPlans() {
  const [plans, setPlans] = useState(null)
  const [form, setForm] = useState(blank)
  const [editing, setEditing] = useState(null) // { id, ...form }
  const [saving, setSaving] = useState(false)

  const load = () => billingAPI.getPlans().then(r => setPlans(r.data.plans)).catch(() => setPlans([]))
  useEffect(() => { load() }, [])

  const create = async e => {
    e.preventDefault()
    const body = toBody(form)
    if (!body.name || !(body.basePriceCents > 0)) return toast.error(t('Enter a plan name and a price'))
    setSaving(true)
    try {
      await billingAPI.createPlan({ ...body, currency: form.currency, interval: 'month' })
      toast.success(t('Plan created'))
      setForm({ ...blank, currency: form.currency })
      load()
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Failed to create the plan'))
    } finally { setSaving(false) }
  }

  const startEdit = p => setEditing({
    id: p.id, name: p.name, price: (p.base_price_cents / 100).toFixed(2), currency: p.currency,
    maxServices: p.max_services == null ? '' : String(p.max_services), maxUsers: p.max_users == null ? '' : String(p.max_users),
    features: parseFeatures(p).join('\n'),
  })

  const saveEdit = async e => {
    e.preventDefault()
    const body = toBody(editing)
    if (!body.name || !(body.basePriceCents > 0)) return toast.error(t('Enter a plan name and a price'))
    setSaving(true)
    try {
      await billingAPI.updatePlan(editing.id, body)
      toast.success(t('Plan updated'))
      setEditing(null)
      load()
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Failed to update the plan'))
    } finally { setSaving(false) }
  }

  const toggle = async plan => {
    try {
      await billingAPI.updatePlan(plan.id, { active: !plan.active })
      load()
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Failed to update the plan'))
    }
  }

  return (
    <div>
      <PageHeader title={t('Plans')} subtitle={t('Monthly prices businesses can subscribe to, with how many services and users each includes. You can still set a custom price for one business from its page.')} />

      <form onSubmit={create} className="mb-6 flex flex-wrap items-end gap-3 rounded-xl bg-white p-4 ring-1 ring-slate-200">
        <PlanFields value={form} onChange={setForm} withCurrency />
        <button disabled={saving} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">{t('Add plan')}</button>
      </form>

      {editing && (
        <form onSubmit={saveEdit} className="mb-6 flex flex-wrap items-end gap-3 rounded-xl bg-indigo-50 p-4 ring-1 ring-indigo-200">
          <p className="basis-full text-sm font-semibold text-indigo-900">{t('Edit plan')}</p>
          <PlanFields value={editing} onChange={setEditing} />
          <button disabled={saving} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">{t('Save')}</button>
          <button type="button" onClick={() => setEditing(null)} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-300 hover:bg-white">{t('Cancel')}</button>
        </form>
      )}

      {!plans ? <Loading /> : !plans.length ? <Empty>{t('No plans yet. Add your first one above.')}</Empty> : (
        <Table head={[t('Plan'), t('Price'), t('Services'), t('Users'), t('Status'), t('Stripe'), '']}>
          {plans.map(p => (
            <tr key={p.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-medium text-slate-900">
                {p.name}
                {parseFeatures(p).length > 0 && <div className="mt-0.5 max-w-xs truncate text-xs font-normal text-slate-500">{parseFeatures(p).join(' · ')}</div>}
              </td>
              <td className="px-4 py-3 tabular-nums">{cents(p.base_price_cents, (p.currency || 'usd').toUpperCase())}{t('/mo')}</td>
              <td className="px-4 py-3">{limitText(p.max_services, t('All'))}</td>
              <td className="px-4 py-3">{limitText(p.max_users, t('No limit'))}</td>
              <td className="px-4 py-3">{p.active ? <Chip tone="green">{t('Available')}</Chip> : <Chip>{t('Hidden')}</Chip>}</td>
              <td className="px-4 py-3">{p.stripe_price_id ? <Chip tone="blue">{t('Linked')}</Chip> : <span className="text-xs text-slate-500">{t('Linked on first checkout')}</span>}</td>
              <td className="px-4 py-3 text-end whitespace-nowrap">
                <button onClick={() => startEdit(p)} className="me-2 rounded-md px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-50">{t('Edit')}</button>
                <button onClick={() => toggle(p)} className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100">{p.active ? t('Hide') : t('Make available')}</button>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  )
}
